// Analytics utility for tracking events across Google Analytics and Google Ads

declare global {
  interface Window {
    gtag: (
      command: "config" | "event" | "js",
      targetId: string | Date,
      config?: {
        [key: string]: any;
        send_to?: string;
      },
    ) => void;
  }
}

// Same measurement ID as the marketing site (landing-v2/src/layouts/Base.astro) so a
// visit on sync-cal.com and the signup on app.sync-cal.com share one GA4 session.
const GA4_MEASUREMENT_ID = "G-ZBCZN4CJ0H";
const GOOGLE_ADS_ID = "AW-11038710768"; // Your existing Google Ads ID

export interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
  customParameters?: Record<string, any>;
}

/**
 * Track an event in both Google Analytics 4 and Google Ads
 */
export const trackEvent = (event: AnalyticsEvent) => {
  if (typeof window === "undefined" || !window.gtag) {
    console.warn("Google Analytics not available");
    return;
  }

  // Track in Google Analytics 4
  window.gtag("event", event.action, {
    event_category: event.category,
    event_label: event.label,
    value: event.value,
    ...event.customParameters,
    send_to: GA4_MEASUREMENT_ID,
  });

  // Track in Google Ads (for conversion events)
  if (event.category === "conversion") {
    window.gtag("event", "conversion", {
      send_to: `${GOOGLE_ADS_ID}/${event.label}`,
      ...event.customParameters,
    });
  }
};

/**
 * Set user properties for Google Analytics
 */
export const setUserProperties = (userId: string) => {
  if (typeof window === "undefined" || !window.gtag) {
    console.warn("Google Analytics not available");
    return;
  }

  // Check if user properties are already set to avoid duplicate calls
  const currentUserId = localStorage.getItem("analytics_user_id");
  if (currentUserId === userId) {
    return; // Already set for this user
  }

  window.gtag("config", GA4_MEASUREMENT_ID, {
    user_id: userId,
  });

  // Store the user ID to prevent duplicate calls
  localStorage.setItem("analytics_user_id", userId);
};

/**
 * Clear user properties (call on logout)
 */
export const clearUserProperties = () => {
  if (typeof window === "undefined" || !window.gtag) {
    return;
  }

  // Clear the stored user ID
  localStorage.removeItem("analytics_user_id");

  // Reset user properties in GA
  window.gtag("config", GA4_MEASUREMENT_ID, {
    user_id: undefined,
  });
};

/**
 * Track page views
 */
export const trackPageView = (page: string) => {
  if (typeof window === "undefined" || !window.gtag) {
    return;
  }

  window.gtag("config", GA4_MEASUREMENT_ID, {
    page_path: page,
  });
};

/**
 * Track feature usage
 */
export const trackFeatureUsage = (feature: string) => {
  trackEvent({
    action: "feature_used",
    category: "engagement",
    label: feature,
    customParameters: {
      feature_name: feature,
    },
  });
};

export type SignUpMethod = "email" | "google";

/**
 * Track a completed account creation (GA4 recommended event `sign_up`)
 */
export const trackSignUp = (method: SignUpMethod) => {
  trackEvent({
    action: "sign_up",
    category: "acquisition",
    label: method,
    customParameters: { method },
  });
};

const trackedCalendarIds = new Set<string>();

/**
 * Track a calendar successfully connected (not a reauthorization).
 * Pass calendarId when the backend returns it: a retry or repeated submit that
 * upserts the same calendar is then counted once per page session.
 */
export const trackCalendarConnected = (
  provider: string,
  calendarId?: string,
) => {
  if (calendarId) {
    if (trackedCalendarIds.has(calendarId)) {
      return;
    }
    trackedCalendarIds.add(calendarId);
  }

  trackEvent({
    action: "calendar_connected",
    category: "activation",
    label: provider,
    customParameters: { calendar_provider: provider },
  });
};

/**
 * Track error
 */
export const trackError = (error: string, context?: string) => {
  trackEvent({
    action: "exception",
    category: "error",
    label: error,
    customParameters: {
      description: error,
      context: context,
    },
  });
};
